import { useEffect, useState } from 'react';

import { StepProps, useAutoNavigate, useFlowState } from '@betterment/js-runtime';
import { Banner, Box, Button, H1, Table, Text } from '@betterment/samba-react';
import { Form, Formik, FormikHelpers } from 'formik';

import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { useCreateEmployeeContributionConfigChangeMutation } from '../hooks';
import { DeferralSettings, DeferralSettingsFlowState } from '../types';
import { toFormattedMoney } from '../utils';

type ReviewFormValues = {
  priorRate: DeferralSettings;
  newRate: DeferralSettings;
  employeeId: string;
  employerName: string;
};

function formatValue({ value, type }: { value: number; type: 'amount' | 'percent' }) {
  if (type === 'amount') {
    return `${toFormattedMoney(value)}`;
  }

  return `${value}%`;
}

export const ReviewSelectionsStep: React.FC<StepProps> = () => {
  const navigate = useAutoNavigate();
  const [hasError, setHasError] = useState(false);

  const {
    flowState: {
      values: { priorRate, newRate, employeeId, employerName },
      returnToPath,
    },
    goToStep,
    goToPreviousStep,
  } = useFlowState<DeferralSettingsFlowState>();

  const { submitEmployeeContributionConfigChange, data, loading, called, error } =
    useCreateEmployeeContributionConfigChangeMutation();

  useEffect(() => {
    if (!priorRate) {
      goToPreviousStep();
    }
  }, [priorRate, goToPreviousStep]);

  const handleSubmit = async (values: ReviewFormValues, { setSubmitting }: FormikHelpers<ReviewFormValues>) => {
    setHasError(false);
    try {
      await submitEmployeeContributionConfigChange(values.newRate, values.employeeId);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const payload = data?.createEmployeeContributionConfigurationChangeRequest;
    const hasNoErrors = payload?.displayableErrors?.length === 0;
    const hasDisplayableErrors = payload?.displayableErrors && payload.displayableErrors.length > 0;

    if (called && !loading && payload && hasNoErrors) {
      goToStep('success');
    }

    if (called && !loading && (hasDisplayableErrors || error)) {
      setHasError(true);
    }

    // adding goToStep to the dependency array causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [called, loading, data, error]);

  if (!priorRate) {
    return null;
  }

  const initialValues: ReviewFormValues = {
    priorRate,
    newRate,
    employeeId,
    employerName,
  };

  return (
    <WorkflowLayout
      pageTitle="Set your 401(k) contributions"
      workflowTitle="Set your 401(k) contributions"
      size="full"
      onClose={() => navigate(returnToPath)}
    >
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {({ isSubmitting, values }) => (
          <Form>
            <Box css={{ maxWidth: '600px', margin: '0 auto', padding: 'space.400' }}>
              <Box css={{ pb: 'space.400' }}>
                <Text variant="text200" css={{ pb: 'space.50' }}>
                  {values.employerName}
                </Text>
                <H1 variant="pageTitle" css={{ pb: 'space.200' }}>
                  Review your updated contributions.
                </H1>
                <Text as="p" css={{ pb: 'space.200' }}>
                  After you approve, we'll notify your employer of the change. It may take up to one payroll period to
                  go into effect, depending on your employer's payroll processing schedule.
                </Text>
              </Box>

              <Box>
                <Table>
                  <Table.Header>
                    <Table.RowHeaderCell css={{ pl: 0 }}>
                      <H1 variant="title100">Base pay (per pay period)</H1>
                    </Table.RowHeaderCell>
                    <Table.RowHeaderCell css={{ textAlign: 'end' }}>Prior rate</Table.RowHeaderCell>
                    <Table.RowHeaderCell css={{ textAlign: 'end' }}>New rate</Table.RowHeaderCell>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row css={{ border: 'none !important' }}>
                      <Table.Cell css={{ pb: 0, pl: 0, width: '55%' }}>Traditional 401(k)</Table.Cell>
                      <Table.Cell css={{ textAlign: 'end', pb: 0, color: 'text.weak' }}>
                        {formatValue(values.priorRate.base.traditional)}
                      </Table.Cell>
                      <Table.Cell css={{ textAlign: 'end', pb: 0 }}>
                        {formatValue(values.newRate.base.traditional)}
                      </Table.Cell>
                    </Table.Row>
                    {values.priorRate.base.roth && values.newRate.base.roth && (
                      <Table.Row css={{ border: 'none !important' }}>
                        <Table.Cell css={{ pb: 'space.500', pl: 0, width: '55%' }}>Roth 401(k)</Table.Cell>
                        <Table.Cell css={{ textAlign: 'end', color: 'text.weak' }}>
                          {formatValue(values.priorRate.base.roth)}
                        </Table.Cell>
                        <Table.Cell css={{ textAlign: 'end' }}>{formatValue(values.newRate.base.roth)}</Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </Box>

              {values.newRate.bonus && values.priorRate.bonus && (
                <Box>
                  <Table>
                    <Table.Header>
                      <Table.RowHeaderCell css={{ pt: 0, pl: 0 }}>
                        <H1 variant="title100">Bonus</H1>
                      </Table.RowHeaderCell>
                    </Table.Header>
                    <Table.Body>
                      <Table.Row css={{ border: 'none !important' }}>
                        <Table.Cell css={{ pb: 0, pl: 0, width: '55%' }}>Traditional 401(k)</Table.Cell>
                        <Table.Cell css={{ textAlign: 'end', pb: 0, color: 'text.weak' }}>
                          {formatValue(values.priorRate.bonus.traditional)}
                        </Table.Cell>
                        <Table.Cell css={{ textAlign: 'end', pb: 0 }}>
                          {formatValue(values.newRate.bonus.traditional)}
                        </Table.Cell>
                      </Table.Row>
                      {values.priorRate.bonus.roth && values.newRate.bonus.roth && (
                        <Table.Row css={{ border: 'none !important' }}>
                          <Table.Cell css={{ pb: 'space.500', pl: 0, width: '55%' }}>Roth 401(k)</Table.Cell>
                          <Table.Cell css={{ textAlign: 'end', color: 'text.weak' }}>
                            {formatValue(values.priorRate.bonus.roth)}
                          </Table.Cell>
                          <Table.Cell css={{ textAlign: 'end' }}>{formatValue(values.newRate.bonus.roth)}</Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                </Box>
              )}

              {hasError && (
                <Banner data-testid="errorBanner" variant="critical" css={{ mt: 'space.400' }}>
                  <Banner.Content>
                    <Text>
                      We're unable to save these rates. Please try again later. If the issue continues,{' '}
                      <a href="mailto:support@betterment.com">contact us</a>.
                    </Text>
                  </Banner.Content>
                  <Banner.Close onClick={() => setHasError(false)} />
                </Banner>
              )}

              <Box css={{ display: 'flex', gap: 'space.200', pb: 'space.600', pt: 'space.400' }}>
                <Button type="submit" disabled={loading || isSubmitting} loading={isSubmitting}>
                  Approve
                </Button>
                <Button onClick={() => goToPreviousStep()} variant="solidSecondary" disabled={isSubmitting}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </WorkflowLayout>
  );
};
